import { IMG, CONFLUENCE_DAUGHTERS } from "@/lib/data";
import { RevealLines, RevealWords } from "@/components/RevealText";
import { Reveal, StaggerContainer, StaggerItem } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

function HeifersHero() {
  return (
    <section className="relative bg-bone text-ink pt-40 md:pt-48 pb-16">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-8 items-end">
        <div className="col-span-12 md:col-span-7">
          <RevealWords
            text="— Replacement Heifers"
            className="font-mono text-[11px] tracking-overline uppercase text-sage"
          />
          <RevealLines
            as="h1"
            lines={["Heifers built", "to raise a", "calf every year."]}
            className="mt-8 font-serif text-[13vw] md:text-[8.6vw] lg:text-[7.4vw] leading-[0.92] tracking-tightest text-ink"
            delay={0.2}
            stagger={0.12}
          />
        </div>
        <Reveal className="col-span-12 md:col-span-4 md:col-start-9" delay={0.35}>
          <p className="text-ink/75 leading-relaxed max-w-[42ch]">
            Structurally sound and developed on grass. We select for
            maternal expression first — udders, feet and disposition.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function HeifersImage() {
  return (
    <section className="relative bg-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-8">
        <Reveal>
          <div className="clip-cinematic relative overflow-hidden">
            <img
              src={IMG.angusHeifer}
              alt="Registered Angus heifer close-up portrait"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 pointer-events-none noise-overlay-light" />
          </div>
          <p className="mt-3 font-mono text-[10px] tracking-overline uppercase text-sage">
            Angus heifer, spring grass.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ConfluenceDaughters() {
  return (
    <section className="relative bg-obsidian text-bone noise-overlay overflow-hidden">
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <Reveal className="col-span-12 md:col-span-6" y={30}>
            <div className="relative overflow-hidden border border-bone/10">
              <img
                src={IMG.confluenceHeifers}
                alt="Registered Confluence 4273 daughters — Hideaway Donna 502 & 503"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 pointer-events-none noise-overlay" />
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-overline uppercase text-limestone">
              Confluence daughters on the tanbark.
            </p>
          </Reveal>

          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                — By the Flagship · Confluence 4273 Daughters
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-bone">
                Proven maternal <br />
                <span className="italic text-limestone">from the ground up.</span>
              </h2>
              <p className="mt-6 text-limestone leading-relaxed max-w-[50ch]">
                A set of registered females sired by our flagship, VDAR Confluence
                4273 — deep-bodied, structurally correct and bred to become the
                cornerstone of a maternal herd.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              {CONFLUENCE_DAUGHTERS.map((h, i) => (
                <Reveal key={h.name} delay={0.05 * i} y={20}>
                  <div className="border border-bone/15 p-6 md:p-7">
                    <div className="flex items-baseline justify-between gap-4">
                      <div className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                        {h.lot}
                      </div>
                      <div className="font-mono text-[10px] tracking-overline uppercase text-limestone">
                        {h.reg}
                      </div>
                    </div>
                    <h3 className="mt-3 font-serif text-3xl md:text-4xl text-bone tracking-tight">
                      {h.name}
                    </h3>
                    <div className="mt-5 grid grid-cols-3 border-t border-bone/10 divide-x divide-bone/10">
                      <div className="pt-4 pr-3">
                        <div className="font-mono text-[10px] tracking-overline uppercase text-limestone">
                          DOB
                        </div>
                        <div className="mt-1.5 font-sans text-sm text-bone">
                          {h.dob}
                        </div>
                      </div>
                      <div className="pt-4 px-3">
                        <div className="font-mono text-[10px] tracking-overline uppercase text-limestone">
                          Sire
                        </div>
                        <div className="mt-1.5 font-sans text-sm text-bone">
                          {h.sire}
                        </div>
                      </div>
                      <div className="pt-4 pl-3">
                        <div className="font-mono text-[10px] tracking-overline uppercase text-limestone">
                          Dam
                        </div>
                        <div className="mt-1.5 font-sans text-sm text-bone">
                          {h.dam}
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Standards() {
  const rows = [
    ["Age", "Yearling to bred-heifer"],
    ["Breeding", "Program-sire mated"],
    ["Selection", "Structural correctness first"],
    ["Disposition", "Quiet handling, hand-fed"],
    ["Nutrition", "Grass developed, no push"],
    ["Availability", "[Sale window here]"],
  ];
  return (
    <section className="relative bg-parchment">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-start">
          <Reveal className="col-span-12 md:col-span-4">
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              — Selection Standard
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[1]">
              Every heifer earns her place.
            </h2>
          </Reveal>
          <div className="col-span-12 md:col-span-8">
            <StaggerContainer className="divide-y divide-border_subtle border-y border-border_subtle">
              {rows.map(([k, v]) => (
                <StaggerItem
                  key={k}
                  className="grid grid-cols-12 py-6 gap-4 items-baseline"
                >
                  <span
                    aria-hidden
                    className="col-span-2 md:col-span-1 font-mono text-sage"
                  >
                    —
                  </span>
                  <span className="col-span-10 md:col-span-4 font-serif text-2xl text-ink">
                    {k}
                  </span>
                  <span className="col-span-12 md:col-span-7 text-ink/75 leading-relaxed">
                    {v}
                  </span>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative bg-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end">
          <Reveal className="col-span-12 md:col-span-8">
            <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
              — Ask about availability
            </p>
            <h3 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-ink leading-[1]">
              Building a maternal herd? <br />
              <span className="italic text-sage">We&apos;d like to help.</span>
            </h3>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-4" y={20}>
            <Link
              to="/contact"
              className="inline-flex w-full items-center justify-between px-6 py-4 bg-ink text-bone text-[12px] uppercase tracking-overline hover:bg-terracotta transition-colors"
              data-testid="heifers-inquiry-cta"
            >
              Inquire about heifers
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Heifers() {
  return (
    <div className="bg-bone">
      <HeifersHero />
      <EditorialMarquee
        phrases={[
          "Replacement Heifers",
          "Maternal First",
          "Structurally Sound",
          "Bitterroot Valley",
          "Registered Seedstock",
        ]}
      />
      <HeifersImage />
      <ConfluenceDaughters />
      <Standards />
      <CTA />
    </div>
  );
}
